import { messageSchema } from "@/controllers/channel/schema";
import { queryClient } from "@/lib/query-client";
import { useQuery as useQueryTanstack } from "@tanstack/react-query";

import { z } from "zod";

export namespace ChatMessagesLatestMessageQuery {
  export const keys = {
    all: ["chat", "messages"] as const,
    channel: (channelId: string) => [...keys.all, channelId] as const,
  } as const;

  export type TData = z.infer<typeof messageSchema> | null;
  type QueryKey = typeof keys.all | ReturnType<typeof keys.channel>;

  export const updaters = {
    updateMessage: (data: TData) => (oldData: TData | null) => {
      if (!oldData) {
        return oldData;
      }

      return {
        ...oldData,
        ...data,
      };
    },
  };

  export function invalidate(queryKey: QueryKey) {
    return queryClient.invalidateQueries({
      queryKey: [queryKey],
    });
  }

  export function getData(channelId?: string) {
    if (!channelId) {
      return queryClient.getQueryData<TData>(keys.all) ?? null;
    }

    return queryClient.getQueryData<TData>(keys.channel(channelId)) ?? null;
  }

  export function setData(channelId: string | undefined, data: TData) {
    if (!channelId) {
      return queryClient.setQueryData(keys.all, data);
    }

    return queryClient.setQueryData(keys.channel(channelId), data);
  }

  export function updateData(
    channelId: string | undefined,
    updater: (oldData: TData | null) => TData | null,
  ) {
    if (!channelId) {
      return queryClient.setQueryData(keys.all, updater);
    }

    queryClient.setQueryData(keys.channel(channelId), updater);
  }

  export function useQuery(channelId?: string) {
    const queryKey = channelId ? keys.channel(channelId) : keys.all;

    return useQueryTanstack({
      queryKey: queryKey,
      queryFn: () => {
        return new Promise<TData>((resolve) => {
          resolve(getData(channelId));
        });
      },
      staleTime: 0,
      gcTime: 0,
    });
  }
}
